import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import { useSession } from "../lib/auth-client";
import { consumeReturnTo, readReturnTo } from "../lib/auth-return";
import { handoffTarget, withHandoff } from "../lib/preview-signin";
import { clearRbacCache } from "../hooks/use-rbac";
import { PreviewHandoffConfirm } from "../components/PreviewHandoffConfirm";

/**
 * The OAuth GET callback redirects here after better-auth has issued the
 * session cookie. A full-page replace then loads the original deep link with
 * that cookie visible to useSession().
 *
 * #5250: on the staging slot with `?handoff=<preview host>&nonce_hash=`, the
 * sign-in was started on a PR preview. Round 2: the slot shows the confirm
 * naming that host and the signed-in email, and asks for nothing until it is
 * pressed; the button then asks for a one-time code and sends the browser to
 * the preview's claim.
 */
export function AuthReturn() {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const returnTo = readReturnTo(location.search);
  const handoff = handoffTarget(location.search);
  const handingOff = handoff !== null;
  const redirectStarted = useRef(false);

  useEffect(() => {
    // StrictMode runs mount effects twice in development. Guard the consume +
    // replace pair so the second pass cannot consume "/" and overwrite the
    // real destination before navigation completes.
    if (!isPending && session?.user && !redirectStarted.current) {
      redirectStarted.current = true;
      // #4952 round 3: a sign-in can be a different person from the last one
      // on this browser; the deep link loads with no RBAC warm-load cache.
      clearRbacCache();
      // A hand-off waits for the confirm below; nothing moves on by itself.
      if (handingOff) return;
      window.location.replace(consumeReturnTo(location.search));
    }
  }, [handingOff, isPending, location.search, session?.user]);

  if (!isPending && !session?.user) {
    return <Navigate to={withHandoff("/login", returnTo, handoff)} replace />;
  }

  if (handoff && session?.user) {
    return <PreviewHandoffConfirm handoff={handoff} email={session.user.email} returnTo={returnTo} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-muted-foreground text-sm">Completing sign-in...</div>
    </div>
  );
}
